import React from "react";
import styled from "styled-components";
import { Table, Tag, Button, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { KpiBatchInfoItem } from "../../types/kpiBatchInfo";
import {
  formatBatchDate,
  formatRangeLabel,
  checkBatchFreshness,
} from "../../utils/kpiBatchUtils";

const KpiTitle = styled.div`
  font-weight: 600;
  color: #111827;
  font-size: 13px;
`;

const DateCell = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.3;
  .formatted {
    font-size: 12px;
    font-weight: 500;
    color: #111827;
  }
  .relative {
    font-size: 11px;
    color: #6b7280;
  }
`;

const ErrorText = styled.span`
  color: #dc2626;
  font-size: 12px;
  cursor: pointer;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover {
    text-decoration: underline;
  }
`;

const CategoryTag = styled(Tag)`
  font-weight: 600;
  border-radius: 4px;
`;

type Props = {
  items: KpiBatchInfoItem[];
  loading?: boolean;
  referenceDate?: string | Date;
  onViewItem: (item: KpiBatchInfoItem) => void;
};

const getCategoryColor = (category: string): string => {
  const upper = (category || "").toUpperCase();
  if (upper.includes("COMMERCIAL")) return "blue";
  if (upper.includes("OPERATIONAL") || upper.includes("OPERATIONNEL")) return "cyan";
  if (upper.includes("ECOM") || upper.includes("DRIVE")) return "purple";
  if (upper.includes("CLIENT") || upper.includes("NPS")) return "geekblue";
  return "default";
};

const AdminBatchKpiTable: React.FC<Props> = ({
  items,
  loading,
  referenceDate,
  onViewItem,
}) => {
  const { t } = useTranslation();

  const columns: ColumnsType<KpiBatchInfoItem> = [
    {
      title: t("admin.table.status", "Statut"),
      dataIndex: "status",
      key: "status",
      width: 130,
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (status: string) => {
        const isSuccess = status === "SUCCESS";
        return (
          <Tag
            color={isSuccess ? "success" : "error"}
            icon={isSuccess ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            style={{ fontWeight: 600, padding: "2px 8px" }}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: t("admin.table.category", "Catégorie"),
      dataIndex: "kpiCategory",
      key: "kpiCategory",
      width: 150,
      sorter: (a, b) => (a.kpiCategory || "").localeCompare(b.kpiCategory || ""),
      render: (cat: string) => (
        <CategoryTag color={getCategoryColor(cat)}>
          {cat || "AUTRE"}
        </CategoryTag>
      ),
    },
    {
      title: t("admin.table.kpi", "KPI"),
      dataIndex: "kpiName",
      key: "kpiName",
      sorter: (a, b) => (a.kpiName || "").localeCompare(b.kpiName || ""),
      render: (name: string, record) => (
        <div style={{ cursor: "pointer" }} onClick={() => onViewItem(record)}>
          <KpiTitle>{name}</KpiTitle>
        </div>
      ),
    },
    {
      title: t("admin.table.range", "Temporalité"),
      dataIndex: "range",
      key: "range",
      width: 150,
      sorter: (a, b) => (a.range || "").localeCompare(b.range || ""),
      render: (range: string) => (
        <Tooltip title={`Code: ${range}`}>
          <Tag color="default" style={{ borderRadius: "4px" }}>
            {formatRangeLabel(range)}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: t("admin.table.country", "Pays"),
      dataIndex: "countryKey",
      key: "countryKey",
      width: 80,
      render: (country: string) => <Tag color="blue">{country || "FRA"}</Tag>,
    },
    {
      title: t("admin.table.run_at", "Date d'exécution"),
      dataIndex: "runAt",
      key: "runAt",
      width: 210,
      defaultSortOrder: "descend",
      sorter: (a, b) => {
        const timeA = a.runAt ? new Date(a.runAt).getTime() : 0;
        const timeB = b.runAt ? new Date(b.runAt).getTime() : 0;
        return timeA - timeB;
      },
      render: (runAt: string, record) => {
        const d = formatBatchDate(runAt);
        const freshness = checkBatchFreshness(runAt, record.range, referenceDate);
        return (
          <DateCell>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span className="formatted">{d.formatted}</span>
              {freshness.isWarning && (
                <Tooltip title={freshness.message}>
                  <Tag
                    color="warning"
                    icon={<WarningOutlined />}
                    style={{ margin: 0, padding: "0 6px", fontSize: "11px", fontWeight: 600 }}
                    data-testid="warning-tag"
                  >
                    {t("admin.warning", "Retard")}
                  </Tag>
                </Tooltip>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {d.relative && <span className="relative">{d.relative}</span>}
              {freshness.isWarning && (
                <span
                  style={{ fontSize: "11px", color: "#d97706", fontWeight: 500 }}
                  title={freshness.message}
                >
                  • {freshness.shortMessage}
                </span>
              )}
            </div>
          </DateCell>
        );
      },
    },
    {
      title: t("admin.table.error", "Erreur / Info"),
      dataIndex: "error",
      key: "error",
      render: (error: string | null, record) => {
        if (!error) {
          return <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: "12px" }}>—</span>;
        }
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <ErrorText onClick={() => onViewItem(record)}>
              {error}
            </ErrorText>
            <Button
              size="small"
              danger
              type="link"
              style={{ padding: 0, fontSize: "11px" }}
              onClick={() => onViewItem(record)}
            >
              {t("admin.view_error", "Voir")}
            </Button>
          </div>
        );
      },
    },
    {
      title: t("admin.table.actions", "Actions"),
      key: "actions",
      width: 90,
      fixed: "right",
      render: (_, record) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => onViewItem(record)}
          title={t("admin.details", "Détails")}
        >
          {t("admin.details", "Détails")}
        </Button>
      ),
    },
  ];

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={items}
      loading={loading}
      pagination={{
        defaultPageSize: 15,
        pageSizeOptions: ["15", "25", "50", "100"],
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} batchs`,
      }}
      scroll={{ x: 1000 }}
      size="middle"
    />
  );
};

export default AdminBatchKpiTable;
